import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { AppShell, TopNav, TopBar, LeftNav, BodyGrid, PageBody } from '../components/Layout';
import { apiFetch } from '../services/api';
import { useWorkspaces } from '../hooks/useWorkspaces';

function timeAgo(ts) {
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const FILTER_OPTIONS = [
  { value: 'all',    label: 'All' },
  { value: 'false',  label: 'Unread' },
  { value: 'true',   label: 'Read'   },
];

export default function CeoMessagesPage() {
  const [searchParams]  = useSearchParams();
  const { activeWorkspace } = useWorkspaces();

  // Prefer workspace_id from URL, fall back to activeWorkspace
  const workspaceId = searchParams.get('workspace_id') || activeWorkspace?.id || '';

  const [messages,    setMessages]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [filter,      setFilter]      = useState('all');   // 'all' | 'true' | 'false'
  const [text,        setText]        = useState('');
  const [sending,     setSending]     = useState(false);
  const [deletingId,  setDeletingId]  = useState(null);
  const [error,       setError]       = useState(null);

  // ── Fetch messages ────────────────────────────────────────────────────────
  const fetchMessages = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const qs  = filter !== 'all' ? `?is_read=${filter}` : '';
      const data = await apiFetch(`/api/workspaces/${workspaceId}/messages${qs}`);
      setMessages(Array.isArray(data) ? data : data?.messages ?? []);
    } catch (e) {
      setError('Failed to load messages.');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, filter]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  // ── Send message ──────────────────────────────────────────────────────────
  async function handleSend() {
    if (!text.trim() || sending || !workspaceId) return;
    setSending(true);
    setError(null);
    try {
      const msg = await apiFetch(`/api/workspaces/${workspaceId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ message_text: text.trim() }),
      });
      setText('');
      // Prepend optimistically then re-fetch to get server state
      setMessages(prev => [msg, ...prev]);
    } catch {
      setError('Failed to send message.');
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend();
  }

  // ── Delete message ────────────────────────────────────────────────────────
  async function handleDelete(messageId) {
    setDeletingId(messageId);
    try {
      await apiFetch(`/api/workspaces/${workspaceId}/messages/${messageId}`, {
        method: 'DELETE',
      });
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch {
      setError('Failed to delete message.');
    } finally {
      setDeletingId(null);
    }
  }

  const crumbs = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'CEO Messages' },
  ];

  return (
    <AppShell>
      <style>{`
        .msg-row { transition: background 0.12s; }
        .msg-row:hover { background: var(--paper-2) !important; }
        .msg-row:hover .delete-btn { opacity: 1 !important; }
        .delete-btn { opacity: 0; transition: opacity 0.15s; }
        @media (max-width: 640px) {
          .msg-compose-row { flex-direction: column !important; }
        }
      `}</style>

      <TopNav />
      <TopBar />
      <BodyGrid>
        <LeftNav />
        <PageBody>

          {/* ── Page header ── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h1 style={{ fontFamily: 'var(--serif)', fontSize: '24px', fontWeight: '400', marginBottom: '2px' }}>
                CEO Messages
              </h1>
              <p style={{ fontSize: '13px', color: 'var(--ink-mute)' }}>
                Send instructions or questions directly to your CEO agent.
              </p>
            </div>
            <Link to="/dashboard" style={{ fontSize: '12px', color: 'var(--ink-soft)', textDecoration: 'none' }}>
              ← Back to dashboard
            </Link>
          </div>

          {/* ── Compose area ── */}
          <div style={{
            background: 'var(--white)', border: '1px solid var(--paper-3)',
            borderRadius: 'var(--r)', padding: '16px', marginBottom: '20px',
          }}>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Write a message to your CEO agent… (⌘↵ to send)"
              rows={3}
              style={{
                width: '100%', boxSizing: 'border-box',
                border: '1px solid var(--paper-3)', borderRadius: 'var(--r)',
                padding: '10px 12px', fontFamily: 'var(--sans)', fontSize: '13px',
                color: 'var(--ink)', background: 'var(--paper)',
                resize: 'vertical', outline: 'none', lineHeight: 1.55,
                marginBottom: '10px',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }} className="msg-compose-row">
              <button
                onClick={() => setText('')}
                disabled={!text.trim()}
                style={{
                  padding: '7px 14px', background: 'none',
                  border: '1px solid var(--paper-3)', borderRadius: 'var(--r)',
                  fontFamily: 'var(--sans)', fontSize: '12px', cursor: text.trim() ? 'pointer' : 'default',
                  color: 'var(--ink-mute)', opacity: text.trim() ? 1 : 0.4,
                }}
              >
                Clear
              </button>
              <button
                onClick={handleSend}
                disabled={sending || !text.trim()}
                style={{
                  padding: '7px 18px',
                  background: sending || !text.trim() ? 'var(--paper-3)' : 'var(--ink)',
                  color: sending || !text.trim() ? 'var(--ink-mute)' : 'var(--paper)',
                  border: 'none', borderRadius: 'var(--r)',
                  fontFamily: 'var(--sans)', fontSize: '13px', fontWeight: '600',
                  cursor: sending || !text.trim() ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                {sending ? (
                  <>
                    <span style={{
                      display: 'inline-block', width: '11px', height: '11px',
                      border: '2px solid var(--ink-mute)', borderTopColor: 'transparent',
                      borderRadius: '50%', animation: 'spin 0.7s linear infinite',
                    }} />
                    Sending…
                  </>
                ) : 'Send to CEO →'}
              </button>
            </div>
          </div>

          {/* ── Filter pills ── */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            {FILTER_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setFilter(opt.value)}
                style={{
                  padding: '5px 14px', borderRadius: '100px', fontSize: '12px',
                  fontWeight: filter === opt.value ? '600' : '400',
                  border: '1px solid',
                  borderColor: filter === opt.value ? 'var(--ink)' : 'var(--paper-3)',
                  background: filter === opt.value ? 'var(--ink)' : 'var(--white)',
                  color: filter === opt.value ? 'var(--paper)' : 'var(--ink-soft)',
                  cursor: 'pointer', fontFamily: 'var(--sans)', transition: 'all 0.12s',
                }}
              >
                {opt.label}
              </button>
            ))}
            {messages.length > 0 && (
              <span style={{ fontSize: '12px', color: 'var(--ink-mute)', alignSelf: 'center', marginLeft: '4px' }}>
                {messages.length} message{messages.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* ── Error ── */}
          {error && (
            <div style={{
              marginBottom: '12px', padding: '10px 14px',
              background: 'rgba(200,86,58,0.1)', border: '1px solid rgba(200,86,58,0.25)',
              borderRadius: 'var(--r)', fontSize: '13px', color: '#a03020',
              display: 'flex', justifyContent: 'space-between',
            }}>
              {error}
              <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.6 }}>✕</button>
            </div>
          )}

          {/* ── Message list ── */}
          <div style={{
            background: 'var(--white)', border: '1px solid var(--paper-3)',
            borderRadius: 'var(--r)', overflow: 'hidden',
          }}>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--ink-mute)', fontSize: '13px' }}>
                Loading…
              </div>
            ) : messages.length === 0 ? (
              <div style={{ padding: '48px', textAlign: 'center' }}>
                <div style={{ fontSize: '28px', marginBottom: '10px' }}>💬</div>
                <p style={{ fontSize: '13px', color: 'var(--ink-mute)' }}>
                  {filter !== 'all' ? 'No messages match this filter.' : 'No messages yet. Send your first message above.'}
                </p>
              </div>
            ) : (
              messages.map((msg, i) => (
                <div
                  key={msg.id}
                  className="msg-row"
                  style={{
                    padding: '14px 18px',
                    borderBottom: i < messages.length - 1 ? '1px solid var(--paper-2)' : 'none',
                    background: msg.is_read ? 'var(--white)' : 'rgba(55,138,221,0.04)',
                    display: 'flex', alignItems: 'flex-start', gap: '12px',
                  }}
                >
                  {/* Unread dot */}
                  <div style={{
                    width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0, marginTop: '5px',
                    background: msg.is_read ? 'transparent' : '#378add',
                  }} />

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      fontSize: '13px', color: 'var(--ink)', lineHeight: 1.6,
                      margin: '0 0 6px', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      {msg.message_text}
                    </p>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '11px', color: 'var(--ink-mute)' }}>
                        {timeAgo(msg.created_at)}
                      </span>
                      {msg.is_read ? (
                        <span style={{ fontSize: '11px', color: 'var(--ink-mute)' }}>Read</span>
                      ) : (
                        <span style={{
                          fontSize: '10px', fontWeight: '600', padding: '1px 7px',
                          borderRadius: '100px', background: 'rgba(55,138,221,0.12)',
                          color: '#1a5fa0', textTransform: 'uppercase', letterSpacing: '0.04em',
                        }}>Unread</span>
                      )}
                    </div>
                  </div>

                  {/* Delete */}
                  <button
                    className="delete-btn"
                    onClick={() => handleDelete(msg.id)}
                    disabled={deletingId === msg.id}
                    title="Delete message"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--ink-mute)', fontSize: '13px', padding: '2px 6px',
                      borderRadius: '4px', flexShrink: 0,
                      transition: 'color 0.12s, background 0.12s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#a03020'; e.currentTarget.style.background = 'rgba(200,86,58,0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-mute)'; e.currentTarget.style.background = 'none'; }}
                  >
                    {deletingId === msg.id ? '…' : '✕'}
                  </button>
                </div>
              ))
            )}
          </div>

        </PageBody>
      </BodyGrid>
    </AppShell>
  );
}